export const FIELD_TYPES = [
  { value: 'CustomFields::Text', label: 'Short text' },
  { value: 'CustomFields::TextArea', label: 'Long text' },
  { value: 'CustomFields::Select', label: 'Dropdown' },
  { value: 'CustomFields::Checkbox', label: 'Checkbox' },
  { value: 'CustomFields::Date', label: 'Date' },
  { value: 'CustomFields::Number', label: 'Number' },
  { value: 'CustomFields::Email', label: 'Email' },
  { value: 'CustomFields::File', label: 'File upload' }
];

const BUILT_IN_LABELS = {
  'CustomFields::Address': 'Address',
  'CustomFields::Phone': 'Phone'
};

const SELECT = 'CustomFields::Select';

const TEXT_TYPES = ['CustomFields::Text', 'CustomFields::TextArea'];

export const ALLOWED_CHARACTER_OPTIONS = [
  { value: '', label: 'Any characters' },
  { value: 'letters', label: 'Letters only' },
  { value: 'letters_and_spaces', label: 'Letters and spaces' },
  { value: 'letters_numbers_and_spaces', label: 'Letters, numbers and spaces' }
];

export const acceptsInputRules = (type) => TEXT_TYPES.includes(type);

export const FLAGS = [
  { key: 'enabled', label: 'Enabled', description: "Collect this field on this venue's events." },
  { key: 'fan_visible', label: 'Fan visible', description: 'Show this field to fans at checkout. Off means box office only.' },
  { key: 'required', label: 'Required', description: 'Fans must answer before they can check out.' },
  { key: 'box_office_required', label: 'Required at box office', description: 'Staff must answer before they can complete a sale in the Organizer app.' }
];

const EVENT_FLAG_DESCRIPTIONS = {
  enabled: 'Collect this field on this event.'
};

export const flagsFor = (context, { withFanVisible = context !== 'event' } = {}) => FLAGS
  .filter((flag) => withFanVisible || flag.key !== 'fan_visible')
  .map((flag) => (context === 'event' ? { ...flag, description: EVENT_FLAG_DESCRIPTIONS[flag.key] || flag.description } : flag));

const FLAG_DEFAULTS = {
  fan_visible: () => true,
  box_office_required: (field) => Boolean(field.required)
};

export const flagValue = (field, key) => Boolean(field[key] ?? FLAG_DEFAULTS[key]?.(field));

export const isBuiltIn = (field) => Boolean(BUILT_IN_LABELS[field.type]);

export const typeLabel = (type) => (
  BUILT_IN_LABELS[type] || FIELD_TYPES.find((t) => t.value === type)?.label || ''
);

const liveOptions = (field) => (field.custom_field_options || []).filter((o) => !o._destroy);

const isBlank = (value) => !value || !value.trim();

export const fieldMeta = (field) => {
  const parts = [typeLabel(field.type)];
  if (field.type === SELECT) {
    const n = liveOptions(field).length;
    parts.push(`${n} option${n === 1 ? '' : 's'}`);
  }
  if (field.max_length) parts.push(`Max ${field.max_length} characters`);
  if (field.allowed_characters) {
    parts.push(ALLOWED_CHARACTER_OPTIONS.find((o) => o.value === field.allowed_characters)?.label);
  }
  if (field.help_text) parts.push(field.help_text);
  return parts.join(' · ');
};

export const ticketTypeSummary = (field) => {
  if (!field.ticket_type_targeted) return 'All tickets';
  const n = (field.ticket_type_ids || []).length;
  if (n === 0) return 'No ticket types';
  return `${n} ticket type${n === 1 ? '' : 's'}`;
};

export const canSaveCustomField = (draft) => {
  if (isBlank(draft.name)) return false;
  if (draft.ticket_type_targeted && !(draft.ticket_type_ids || []).length) return false;
  if (draft.type === SELECT) return liveOptions(draft).some((o) => !isBlank(o.name));
  return true;
};

const optionAttributes = (options = []) => {
  let position = 1;
  return options.map((o) => {
    if (o._destroy || isBlank(o.name)) return o.id ? { id: o.id, _destroy: true } : null;
    const attrs = { name: o.name.trim(), position: position++ };
    return o.id ? { id: o.id, ...attrs } : attrs;
  }).filter(Boolean);
};

export const askPerLabel = (field) => (field.ask_per === 'ticket' ? 'Per ticket' : 'Per order');

export const customFieldPayload = (draft, owner = null, { withTicketTypes = false, withAskPer = false } = {}) => {
  const payload = {
    name: draft.name.trim(),
    help_text: draft.help_text || '',
    enabled: Boolean(draft.enabled),
    fan_visible: draft.fan_visible !== false,
    required: Boolean(draft.required),
    box_office_required: flagValue(draft, 'box_office_required')
  };

  if (!draft.id) {
    payload.type = draft.type;
    if (owner) Object.assign(payload, { custom_fieldable_type: owner.type, custom_fieldable_id: owner.id });
  }

  if (acceptsInputRules(draft.type)) {
    payload.max_length = draft.max_length ? Number(draft.max_length) : null;
    payload.allowed_characters = draft.allowed_characters || null;
  }

  if (draft.type === SELECT) {
    payload.custom_field_options_attributes = optionAttributes(draft.custom_field_options);
  }

  if (withTicketTypes) {
    payload.targeted_ticket_type_ids = draft.ticket_type_targeted ? draft.ticket_type_ids || [] : [];
  }

  if (withAskPer && !isBuiltIn(draft)) {
    payload.ask_per = draft.ask_per || 'order';
  }

  return payload;
};
